import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmDialogComponent,
  DeleteDialogData
} from '../../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { FetchOptionsStateModel } from '../../../core/state/fetchOptions.state';
import { tap } from 'rxjs';
import { TestJsonModel } from '../../test.json.model';
import { TestService } from '../../test.service';
import { TestActions } from './test.actions';

export interface TestStateModel {
  tests: TestJsonModel[]
}

@State<TestStateModel>({
  name: 'test',
  defaults: {
    tests: []
  }
})
@Injectable()
export class TestState {
  private readonly testService = inject(TestService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  @Selector()
  static getState(state: TestStateModel) {
    return state;
  }

  @Selector()
  static getTests(state: TestStateModel) {
    return state.tests;
  }

  @Selector()
  static getAmountOfTests(state: TestStateModel) {
    return state.tests?.length ?? 0;
  }

  @Selector()
  static testsIsEmpty(state: TestStateModel) {
    return (state.tests?.length ?? 0) <= 0;
  }

  @Action(TestActions.Fetch)
  onFetchTests(ctx: StateContext<TestStateModel>, action: TestActions.Fetch) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);

    const { skipStoreDispatch, ...restOverrides } = action.overrideOptions || {};

    const updatedFetchOptions = {
      ...localFetchOptions,
      ...restOverrides
    };

    return this.testService.getAllWithPagination({}, {
      ...updatedFetchOptions,
      skipStoreDispatch
    }).pipe(
      tap(res => {
        ctx.dispatch(new TestActions.FetchSuccess(res[0]));
      })
    );
  }

  @Action(TestActions.FetchSuccess)
  onFetchTestsSuccess(ctx: StateContext<TestStateModel>, { payload }: TestActions.FetchSuccess) {
    ctx.setState({ tests: payload });
  }

  @Action(TestActions.FetchAppend)
  onFetchAppendTests(ctx: StateContext<TestStateModel>, action: TestActions.FetchAppend) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);

    const { skipStoreDispatch, ...restOverrides } = action.overrideOptions || {};

    const updatedFetchOptions = {
      ...localFetchOptions,
      ...restOverrides
    };

    return this.testService.getAllWithPagination({}, {
      ...updatedFetchOptions,
      skipStoreDispatch
    }).pipe(
      tap(res => {
        ctx.dispatch(new TestActions.FetchSuccessAppend(res[0]));
      })
    );
  }

  @Action(TestActions.FetchSuccessAppend)
  onFetchTestsSuccessAppend(ctx: StateContext<TestStateModel>, { payload }: TestActions.FetchSuccess) {
    const state = ctx.getState();
    ctx.setState({
      tests: [...state.tests, ...payload]
    });
  }

  @Action(TestActions.Delete)
  onDelete(ctx: StateContext<TestJsonModel>, { payload }: TestActions.Delete) {
    return this.dialog.open<ConfirmDialogComponent, DeleteDialogData, boolean>(ConfirmDialogComponent, {
      data: {
        id: payload.id,
        name: payload.name,
        itemType: 'Lernsequenz',
        deleteFn: () => {
          return payload.destroy();
        }
      }
    })
      .afterClosed()
      .pipe(
        tap(success => {
          if (!success) return;
          ctx.dispatch(new TestActions.Fetch());
        })
      );
  }
}
